import { useEffect, useMemo, useRef, useState } from 'react';
import { Section } from '@vnetwork/ui/layout';
import {
  Button,
  Skeleton,
  Textarea,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { useToast } from '@vnetwork/ui/feedback';
import { useMsiEmail, useUpdateMsiEmail, useSendMsiReport } from '@/api/waap';
import { errMessage } from '../waap-util';
import { Labeled, SaveBar, ToggleRow } from './settings-ui';
import styles from './MonthlySecurityReportSettings.module.css';

/** The last `count` months as "YYYY-MM", most recent first. */
function recentMonths(count: number): string[] {
  const out: string[] = [];
  const now = new Date();
  for (let i = 0; i < count; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }
  return out;
}

function toList(text: string): string[] {
  return text
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * Monthly Security Report — automatic monthly delivery (recipients) plus on-demand
 * report generation for the past 6 months. Recipients/toggle are backed by the
 * account /msiEmail config.
 */
export function MonthlySecurityReportSettings({ domainId }: { domainId: string }) {
  const { show } = useToast();
  const { data: config, isLoading } = useMsiEmail(domainId);

  const [enabled, setEnabled] = useState(false);
  const [text, setText] = useState('');
  // Seed from the server only when the record actually changes (by `id`), so a
  // background refetch can't clobber unsaved recipient/toggle edits.
  const syncedId = useRef<number | null>(null);
  useEffect(() => {
    if (!config || config.id === syncedId.current) return;
    syncedId.current = config.id;
    setEnabled(config.enable);
    setText((config.email ?? []).join('\n'));
  }, [config]);

  const months = useMemo(() => recentMonths(6), []);
  // Default to the previous month (most recent fully-elapsed report period).
  const [month, setMonth] = useState('');
  useEffect(() => {
    if (!month && months.length > 1) setMonth(months[1]);
  }, [month, months]);

  const update = useUpdateMsiEmail(domainId, {
    onSuccess: () => show({ tone: 'success', title: 'Monthly Security Report saved' }),
    onError: (e) => show({ tone: 'danger', title: 'Save failed', description: errMessage(e) }),
  });

  const save = () =>
    update.mutate({ id: config?.id ?? 0, enable: enabled, email: toList(text) });

  // Generate + email the selected month's report via the vendor MSI endpoint. The
  // endpoint can return success:true yet fail internally (results.error, zero
  // customers) when the vendor's upstream is down, so surface the real result.
  const report = useSendMsiReport(domainId, {
    onSuccess: (res) => {
      const r = res?.results;
      if (r?.error) {
        show({
          tone: 'warning',
          title: `Report for ${month} not sent`,
          description: `The vendor reported: ${r.error}`,
        });
      } else if ((r?.total_customers ?? 0) > 0 || res?.success) {
        show({
          tone: 'success',
          title: 'Report generated',
          description: `The ${month} report is being generated and emailed to the recipients.`,
        });
      } else {
        show({
          tone: 'info',
          title: 'Report requested',
          description: `The vendor accepted the ${month} request but reported no customers processed.`,
        });
      }
    },
    onError: (e) =>
      show({ tone: 'danger', title: 'Generate failed', description: errMessage(e) }),
  });
  const generate = () => {
    if (month) report.mutate(month);
  };

  if (isLoading) return <Skeleton className={styles.skeleton} />;

  return (
    <Section
      title="Automatic Delivery"
      description="Reports are automatically delivered at 10:00 AM (UTC+8) on the 4th of every month. You can also manually generate reports for the past 6 months."
    >
      <ToggleRow
        title="Enable"
        description="Automatically email the monthly security report to the recipients below."
        checked={enabled}
        onChange={setEnabled}
      />

      {enabled && (
        <div className={styles.fields}>
          <Labeled
            label="Email Recipients"
            hint={
              <>
                Enter email address separated by line breaks (one per line).
                <br />
                These recipients will receive both scheduled and manual reports.
              </>
            }
          >
            <Textarea
              rows={5}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={'example1@domain.com\nexample2@domain.com'}
            />
          </Labeled>

          <Labeled
            label="Generate Report"
            hint="Select a month to generate and email the report. Reports are available for the last 6 months only."
          >
            <div className={styles.row}>
              <Select value={month} onValueChange={setMonth}>
                <SelectTrigger className={styles.monthSelect}>
                  <SelectValue placeholder="Select a month" />
                </SelectTrigger>
                <SelectContent>
                  {months.map((m) => (
                    <SelectItem key={m} value={m}>
                      {m}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="secondary" disabled={!month || report.isPending} onClick={generate}>
                {report.isPending ? 'Generating…' : 'Generate & Send Report'}
              </Button>
            </div>
          </Labeled>
        </div>
      )}

      <SaveBar onSave={save} saving={update.isPending} />
    </Section>
  );
}
